<script>
  import LocationControl from '../LocationControl/LocationControl.svelte';
  import MedicationSearchResultTile from '../MedicationSearchResultTile/MedicationSearchResultTile.svelte';
  import MedicationSearchResultSkeletonTile from '../MedicationSearchResultSkeletonTile/MedicationSearchResultSkeletonTile.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import searchValue from '../../stores/search';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { RecentSearches } from '../../utils/local-storage';
  import { getSearchURL } from '../../utils/search-query.ts';
  
  let {
    medicationSearchResultTile,
    errorText,
    recentSearchesText,
    clearRecentSearchesText,
    popularSearchesText,
    locationControl,
    id,
    lang,
    selected = $bindable(),
    isError,
    searchResults,
    popularMedicationsList,
    locationControlElem = $bindable(undefined),
    parentComponent,
    handleCompareMedicationClick = () => {},
    dataTestId = 'search-overpanel-results',
  } = $props();

  let recentSearches = $state([]);

  try {
    recentSearches = RecentSearches.get();
  } catch {
    recentSearches = [];
  }

  const handleClearButtonClick = (search) => {
    RecentSearches.remove(search);
    try {
      recentSearches = RecentSearches.get();
    } catch {
      recentSearches = [];
    }
  };

  const handleClearAll = () => {
    RecentSearches.clear();
    recentSearches = [];
  };

  const getPopularMedURLs = (meds) => {
    let medsWithUrls;
    try {
      medsWithUrls = meds.map((med) => ({ ...med, url: getSearchURL({ ...med, lang }) }));
      // eslint-disable-next-line no-empty
    } catch {}
    return medsWithUrls;
  };

  let popularMedsWithURLs = $state();
  if (popularMedicationsList && popularMedicationsList.popularMedications) {
    popularMedsWithURLs = getPopularMedURLs(popularMedicationsList.popularMedications);
  }
</script>

<div data-testid={dataTestId}>
  {#if locationControl}
    <LocationControl {...locationControl} inOverpanel={true} bind:locationControl={locationControlElem} />
  {/if}
  <ul {id} class="p-4" class:hidden={!searchResults.length} aria-label="Search Results">
    {#each searchResults as item, i (i)}
      <li id={`search-result-${i}`} tab-index="-1" class="mb-4">
        <MedicationSearchResultTile
          {...item}
          {...medicationSearchResultTile}
          {lang}
          focusCallback={() => (selected = i)}
          blurCallback={() => {
            if (selected === i) {
              selected = undefined;
            }
          }}
          {parentComponent}
          {handleCompareMedicationClick}
        />
      </li>
    {/each}
  </ul>
  {#if isError}
    <div data-testid="{dataTestId}-error-text" class="px-10 py-16 flex-1">
      <p class="text-base leading-snug text-center text-gray-gunmetal">{errorText}</p>
    </div>
  {:else if !searchResults.length}
    <div class="p-4">
      {#if $searchValue}
        <ul>
          {#each Array(5)}
            <li class="mb-4">
              <MedicationSearchResultSkeletonTile />
            </li>
          {/each}
        </ul>
      {:else if recentSearches.length}
        <div class="flex flex-row justify-between items-center mb-2">
          <div class="text-base leading-loose text-blue-dark-navy">{recentSearchesText}</div>
          <button
            class="flex flex-row items-center text-xs text-blue-cerulean font-semibold leading-normal py-2 px-1
            hover-hover:hover:text-blue-darkish hover-hover:hover:underline active:underline active:text-blue-darkish"
            onclick={handleClearAll}
            data-testid="{dataTestId}-clear-all-button"
          >
            {clearRecentSearchesText}
            <svg class="ml-2 w-4 h-4 fill-current inline-block" role="presentation">
              <use xlink:href="{SPRITESHEET_URL}clear-x" />
            </svg>
          </button>
        </div>
        <ul>
          {#each recentSearches as item, index (index)}
            <li class="mb-4">
              <MedicationSearchResultTile 
                {...item} 
                {...medicationSearchResultTile} 
                {lang} 
                {handleClearButtonClick} 
                {handleCompareMedicationClick} 
                {parentComponent} 
              />
            </li>
          {/each}
        </ul>
      {:else if popularMedsWithURLs && popularMedsWithURLs.length}
        <div class="mb-2 text-base leading-loose text-blue-dark-navy">{popularSearchesText}</div>
        <ul class="flex flex-wrap">
          {#each popularMedsWithURLs as { name, url, slug }, index (index)}
            <li class="inline-flex mr-3 mb-3" data-testid="{dataTestId}-popular-medication-item">
              {#if parentComponent !== 'compare'}
                <BaseLink
                  className="px-6 py-1 shadow-medium font-semibold rounded bg-white text-base leading-loose text-center text-blue-navy 
                    relative pseudo-shadow-tight-hover active:shadow-active"
                  {url}
                >
                  {name}
                </BaseLink>
              {:else}
                <button 
                  class="px-6 py-1 shadow-medium font-semibold rounded bg-white text-base leading-loose text-center text-blue-navy 
                    relative pseudo-shadow-tight-hover active:shadow-active"
                  onclick={() => handleCompareMedicationClick({name, slug})}
                >
                  {name}
                </button>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>
